import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUp,
  ArrowDown,
  Users,
  Clock,
  CreditCard,
  Calendar,
  TrendingUp,
  Bell,
  MoreHorizontal,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Activity,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardApi } from "@/services/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const MetricCard = ({
  title,
  value,
  icon: Icon,
  trend,
  percentage,
  subtitle,
  onClick,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  trend?: "up" | "down";
  percentage?: string;
  subtitle?: string;
  onClick?: () => void;
}) => (
  <Card
    onClick={onClick}
    className={`group transition-all duration-200 hover:shadow-lg border-0 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-900/80 dark:border-gray-700/50 ${
      onClick ? "cursor-pointer hover:scale-[1.02] dark:hover:shadow-blue-500/20" : ""
    }`}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 dark:from-blue-900/30 dark:to-blue-800/40 dark:text-blue-400">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
            <div className="flex items-center space-x-2">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            </div>
            {(subtitle || (trend && percentage)) && (
              <div className="flex items-center gap-2 mt-1 whitespace-nowrap">
                {subtitle && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 m-0 whitespace-nowrap">{subtitle}</p>
                )}
                {trend && percentage && (
                  <span className={`flex items-center text-xs whitespace-nowrap ${trend === "up" ? "text-green-600" : "text-red-600"}`}>
                    {trend === "up" ? (
                      <ArrowUp className="h-4 w-4 mr-1 flex-shrink-0" />
                    ) : (
                      <ArrowDown className="h-4 w-4 mr-1 flex-shrink-0" />
                    )}
                    {percentage}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [greeting, setGreeting] = useState("");
  
  // API Data States
  const [basicCount, setBasicCount] = useState<any>(null);
  const [topCollectors, setTopCollectors] = useState<any[]>([]);
  const [upcomingDue, setUpcomingDue] = useState<any[]>([]);
  const [voucherDistribution, setVoucherDistribution] = useState<any>(null);
  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const [collectionOverTime, setCollectionOverTime] = useState<any[]>([]);
  
  // Loading States
  const [loading, setLoading] = useState({
    basicCount: true,
    topCollectors: true,
    upcomingDue: true,
    voucherDistribution: true,
    recentPayments: true,
    collectionOverTime: true,
  });
  
  const [chartPeriod, setChartPeriod] = useState("month");

  // Format chart data for recharts
  const getFormattedChartData = () => {
    if (!collectionOverTime || collectionOverTime.length === 0) {
      return [];
    }

    return collectionOverTime.map((item: any) => ({
      name: item.key,
      value: item.value
    }));
  };

  const chartData = getFormattedChartData();
  const hasData = chartData.length > 0 && chartData.some(item => item.value > 0);
  
  // Fetch dashboard data functions
  const fetchBasicCount = async () => {
    try {
      const response = await dashboardApi.getBasicCount();
      if (response.message === 'success') {
        setBasicCount(response.data);
      }
    } catch (error) {
      console.error('Error fetching basic count:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(prev => ({ ...prev, basicCount: false }));
    }
  };

  const fetchTopCollectors = async () => {
    try {
      const response = await dashboardApi.getTopCollectors();
      if (response.message === 'success') {
        setTopCollectors(response.data.topCollectors || []);
      }
    } catch (error) {
      console.error('Error fetching top collectors:', error);
      toast.error('Failed to load top collectors');
    } finally {
      setLoading(prev => ({ ...prev, topCollectors: false }));
    }
  };

  const fetchUpcomingDue = async () => {
    try {
      const response = await dashboardApi.getUpcomingDue();
      if (response.message === 'success') {
        setUpcomingDue(response.data.upcomingDue || []);
      }
    } catch (error) {
      console.error('Error fetching upcoming due:', error);
      toast.error('Failed to load upcoming collections');
    } finally {
      setLoading(prev => ({ ...prev, upcomingDue: false }));
    }
  };

  const fetchVoucherDistribution = async () => {
    try {
      const response = await dashboardApi.getVoucherDistribution();
      setVoucherDistribution(response);
    } catch (error) {
      console.error('Error fetching voucher distribution:', error);
      toast.error('Failed to load voucher distribution');
    } finally {
      setLoading(prev => ({ ...prev, voucherDistribution: false }));
    }
  };

  const fetchRecentPayments = async () => {
    try {
      const response = await dashboardApi.getRecentPayments();
      if (response.message === 'success') {
        setRecentPayments(response.data.recentPayments || []);
      }
    } catch (error) {
      console.error('Error fetching recent payments:', error);
      toast.error('Failed to load recent payments');
    } finally {
      setLoading(prev => ({ ...prev, recentPayments: false }));
    }
  };

  const fetchCollectionOverTime = async (period: 'day' | 'week' | 'month' = 'month') => {
    try {
      setLoading(prev => ({ ...prev, collectionOverTime: true }));
      const response = await dashboardApi.getCollectionOverTime(period);
      if (response.message === 'success') {
        setCollectionOverTime(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching collection over time:', error);
      toast.error('Failed to load collection chart data');
    } finally {
      setLoading(prev => ({ ...prev, collectionOverTime: false }));
    }
  };

  // Handle chart period change
  const handleChartPeriodChange = (period: string) => {
    setChartPeriod(period.toLowerCase());
    fetchCollectionOverTime(period.toLowerCase() as 'day' | 'week' | 'month');
  };

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");

    // Fetch all dashboard data
    fetchBasicCount();
    fetchTopCollectors();
    fetchUpcomingDue();
    fetchVoucherDistribution();
    fetchRecentPayments();
    fetchCollectionOverTime(chartPeriod as 'day' | 'week' | 'month');
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="p-6 space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900dark:text-white">
              {greeting}, {user?.name || "Foxiom"}
            </h1>
            <p className="text-gray-600 mt-1 dark:text-white">Here's a summary of your collection data</p>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Collections Today"
            value={loading.basicCount ? "Loading..." : basicCount ? `${basicCount.totalCollectionsToday?.toLocaleString() || '0'}` : "₹0"}
            icon={loading.basicCount ? Loader2 : DollarSign}
            trend={basicCount?.totalCollectionsComparison > 0 ? "up" : "down"}
            percentage={basicCount?.totalCollectionsComparison > 0 ? `+${basicCount?.totalCollectionsComparison}%` : `${basicCount?.totalCollectionsComparison}%`}
            subtitle="Compared to Yesterday:"
            onClick={() => navigate("/collection/all-payments")}
          />
          <MetricCard
            title="Pending Amount"
            value={loading.basicCount ? "Loading..." : basicCount ? `${basicCount.pendingAmount?.toLocaleString() || '0'}` : "₹0"}
            icon={loading.basicCount ? Loader2 : Clock}
            subtitle={basicCount ? `Overdue: ${basicCount.overdue?.toLocaleString() || '0'}` : "Overdue: ₹0"}
            onClick={() => navigate("/collection/record-payment")}
          />
          <MetricCard
            title="Total Collected This Month"
            value={loading.basicCount ? "Loading..." : basicCount ? `${basicCount.totalCollectedThisMonth?.toLocaleString() || '0'}` : "₹0"}
            icon={loading.basicCount ? Loader2 : TrendingUp}
            onClick={() => navigate("/collection/all-payments")}
          />
          <MetricCard
            title="Active Collectors"
            value={loading.basicCount ? "Loading..." : basicCount ? basicCount.activeCollectors?.toString() || "0" : "0"}
            icon={loading.basicCount ? Loader2 : Users}
            onClick={() => navigate("/users", { state: { filters: { role: "collector", status: "true" } } })}
          />
        </div>

        {/* Charts and Tables Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Collections Over Time Chart */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-sm h-full flex flex-col">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold">Collections Over Time</CardTitle>
                  </div>
                  <div className="flex space-x-2">
                    {["day", "week", "month"].map((period) => (
                      <Button
                        key={period}
                        variant={chartPeriod === period.toLowerCase() ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleChartPeriodChange(period)}
                        className="h-8 px-3 text-xs"
                        disabled={loading.collectionOverTime}
                      >
                        {loading.collectionOverTime && chartPeriod === period.toLowerCase() ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : null}
                        {period === 'day' ? 'Daily' : period === 'week' ? 'Weekly' : period === 'month' ? 'Monthly' : ''}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                {/* Chart Area using recharts */}
                <div className="h-[300px] w-full">
                  {loading.collectionOverTime ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                      <span className="ml-2 text-gray-500">Loading chart data...</span>
                    </div>
                  ) : !hasData ? (
                    <div className="flex items-center justify-center h-full">
                      <span className="text-gray-500">No data available</span>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="name" 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#888888' }}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#888888' }}
                          tickFormatter={(value) => `${value.toLocaleString()}`}
                        />
                        <Tooltip 
                          formatter={(value: number) => [`${value.toLocaleString()}`, 'Amount']}
                          labelFormatter={(label) => `${label}`}
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}
                        />
                        <Bar 
                          dataKey="value" 
                          radius={[4, 4, 0, 0]}
                        >
                          {chartData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.value > 0 ? '#4f46e5' : 'transparent'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Collections */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-sm h-full flex flex-col dark:bg-gray-800/50 dark:border-gray-700/50">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold dark:text-white">Upcoming Collection</CardTitle>
                  {upcomingDue.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 dark:text-blue-400 p-0 h-auto font-medium text-sm hover:bg-transparent hover:text-blue-600 dark:hover:text-blue-400 !important"
                      onClick={() => navigate("/collection/record-payment")}
                    >
                      View All
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden flex flex-col">
                <div className="overflow-y-auto flex-1 pr-2 -mr-2">
                  {loading.upcomingDue ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                      <span className="ml-2 text-gray-500 text-sm">Loading...</span>
                    </div>
                  ) : upcomingDue.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <span className="text-gray-500 text-sm">No upcoming collections</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {upcomingDue.map((collection, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <div>
                            <p className="font-medium text-sm dark:text-white">{collection.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400"><span>{`${String(new Date(collection.date).getDate()).padStart(2, '0')}/${String(new Date(collection.date).getMonth() + 1).padStart(2, '0')}/${new Date(collection.date).getFullYear()}`}</span></p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-blue-600 dark:text-blue-400">{collection.amount?.toLocaleString() || '0'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom Section - Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment vs Receipt Distribution */}
          <Card className="border-0 shadow-sm dark:bg-gray-800/50 dark:border-gray-700/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold dark:text-white">Payment vs Receipt Distribution</CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Current month voucher breakdown</p>
            </CardHeader>
            <CardContent>
              {loading.voucherDistribution ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-2 text-gray-500">Loading distribution data...</span>
                </div>
              ) : (
                <>
                  {/* Payment vs Receipt Pie Chart */}
                  <div className="flex items-center justify-center mb-6">
                    <div className="relative w-48 h-48 group">
                      {/* Dynamic segments based on API data */}
                      <div className="absolute inset-0 rounded-full shadow-lg transition-all duration-300 transform group-hover:scale-105" style={{
                        background: `conic-gradient(from 0deg, #3b82f6 0deg ${(voucherDistribution?.payments?.percentage || 0) * 3.6}deg, #10b981 ${(voucherDistribution?.payments?.percentage || 0) * 3.6}deg 360deg)`,
                        borderRadius: '50%'
                      }}></div>
                      {/* Inner circle */}
                      <div className="absolute inset-[15px] bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-inner">
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">{voucherDistribution?.total || '0'}</p>
                        </div>
                      </div>
                      
                      {/* Hover tooltips */}
                      {/* Payment tooltip */}
                      <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 -left-20 top-1/4 bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none">
                        <div className="text-center">
                          <p className="font-medium">Payments</p>
                          <p className="font-bold">{voucherDistribution?.payments?.amount || '0'} ({voucherDistribution?.payments?.percentage || 0}%)</p>
                        </div>
                        <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 border-l-8 border-t-8 border-b-8 border-transparent border-l-blue-600"></div>
                      </div>
                      
                      {/* Receipt tooltip */}
                      <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 -right-20 top-1/4 bg-green-600 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none">
                        <div className="text-center">
                          <p className="font-medium">Receipts</p>
                          <p className="font-bold">{voucherDistribution?.receipts?.amount || '0'} ({voucherDistribution?.receipts?.percentage || 0}%)</p>
                        </div>
                        <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 border-r-8 border-t-8 border-b-8 border-transparent border-r-green-600"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="flex justify-center space-x-8 mb-2">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Payments</span>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Receipts</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Collector Performance Table */}
          <Card className="border-0 shadow-sm dark:bg-gray-800/50 dark:border-gray-700/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold dark:text-white">Collector Performance Table</CardTitle>
            </CardHeader>
            <CardContent>
              {loading.topCollectors ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-gray-500 text-sm">Loading collectors...</span>
                </div>
              ) : topCollectors.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <span className="text-gray-500 text-sm">No collector data available</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 pb-2 border-b dark:border-gray-700">
                    <span>Collector Name</span>
                    <span>Total Collection</span>
                  </div>
                  {topCollectors.map((collector, index) => (
                    <div key={index} className="grid grid-cols-2 gap-4 text-sm py-2">
                      <span className="text-gray-900 dark:text-white">{collector.name}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{collector.totalCollected?.toLocaleString() || '0'}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Collections Table (Recent Transactions Feed) */}
        <Card className="border-0 shadow-sm dark:bg-gray-800/50 dark:border-gray-700/50">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between mb-1">
              <CardTitle className="text-lg font-semibold dark:text-white">Collections Table (Recent Transactions Feed)</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 dark:text-blue-400 p-0 h-auto font-medium text-sm hover:bg-transparent hover:text-blue-600 dark:hover:text-blue-400 !important"
                onClick={() => navigate("/collection/all-payments")}
              >
                View All
            </Button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Recent transaction activities</p>
          </CardHeader>
          <CardContent className="px-0 sm:px-6">
            {loading.recentPayments ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-500">Loading recent transactions...</span>
              </div>
            ) : recentPayments.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <span className="text-gray-500">No recent transactions</span>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-full table-auto">
                  <thead>
                    <tr className="text-xs font-medium text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                      <th className="text-left py-2 px-3 whitespace-nowrap">Date</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Consumer Reg No</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Customer Name</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Amount</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Account</th>
                      <th className="text-left py-2 px-3 whitespace-nowrap">Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayments.map((payment, index) => (
                      <tr key={payment.id || index} className="text-sm border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/30">
                        <td className="py-3 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          <span>{`${String(new Date(payment.paymentDate).getDate()).padStart(2, '0')}/${String(new Date(payment.paymentDate).getMonth() + 1).padStart(2, '0')}/${new Date(payment.paymentDate).getFullYear()}`}</span>
                        </td>
                        <td className="py-3 px-3 text-gray-900 dark:text-white whitespace-nowrap font-medium">
                          {payment.collectionRecord?.consumer?.registrationNumber || 'N/A'}
                        </td>
                        <td className="py-3 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {payment.collectionRecord?.consumer?.fullName || 'N/A'}
                        </td>
                        <td className="py-3 px-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                          {payment.amount?.toLocaleString() || '0'}
                        </td>
                        <td className="py-3 px-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                          {payment.creditAccount?.name || 'N/A'}
                        </td>
                        <td className="py-3 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap capitalize">
                          {payment.paymentMethod || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
